import React, { useEffect, useRef } from 'react';
import { T, useTranslate } from '@tolgee/react';
import {
	TrackingDocumentEntry,
	TrackingTruck,
} from '../orderTracking.interface';
import { TRACKING_DOCUMENT_GROUPS } from '../orderTracking.constants';
import {
	formatDateTime,
	truckDocuments,
	truckIdentity,
} from '../orderTracking.utils';
import DocumentIcon from '../../../../assets/DocumentIcon';
import { cn } from '../../../../lib/utils';

interface TrackingDocumentsProps {
	trucks: TrackingTruck[];
	highlight?: { truckId: number; token: number };
	onHighlightEnd?: () => void;
}

const DocumentRow: React.FC<{ doc: TrackingDocumentEntry }> = ({ doc }) => {
	const content = (
		<>
			<span className="w-11 h-11 rounded-md bg-track-surface border border-solid border-track-line box-border flex flex-col items-center justify-center gap-[2px] flex-none transition group-hover:border-[#9A9ABF]">
				<DocumentIcon color="#484880" width={12} height={14} />
				<span className="text-[8px] font-bold tracking-[0.02em] text-track-slate leading-none">
					{doc.extension.toUpperCase()}
				</span>
			</span>
			<span className="min-w-0 flex flex-col">
				<span className="text-sm font-medium text-track-ink truncate transition group-hover:text-track-link">
					{doc.name}
				</span>
				{doc.uploadedAt ? (
					<span className="text-[12.5px] text-track-muted">
						{formatDateTime(doc.uploadedAt)}
					</span>
				) : null}
			</span>
		</>
	);

	return doc.url ? (
		<a
			href={doc.url}
			target="_blank"
			rel="noreferrer"
			className="group flex items-center gap-3 py-2 no-underline"
		>
			{content}
		</a>
	) : (
		<div className="flex items-center gap-3 py-2">{content}</div>
	);
};

const TrackingDocuments: React.FC<TrackingDocumentsProps> = ({
	trucks,
	highlight,
	onHighlightEnd,
}) => {
	const { t } = useTranslate();
	const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});

	useEffect(() => {
		if (!highlight) return;
		cardRefs.current[highlight.truckId]?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	}, [highlight]);

	return (
		<section className="px-4 sm:px-7 py-[22px] flex flex-col gap-4">
			{!trucks.length ? (
				<div className="text-sm text-track-muted">
					<T keyName="orderTracking.noTrucks">
						No trucks assigned to this order yet
					</T>
				</div>
			) : null}
			{trucks.map((truck) => (
				<div
					key={
						highlight?.truckId === truck.id
							? `${truck.id}-${highlight.token}`
							: truck.id
					}
					ref={(node) => {
						cardRefs.current[truck.id] = node;
					}}
					onAnimationEnd={
						highlight?.truckId === truck.id ? onHighlightEnd : undefined
					}
					className={cn(
						'border border-solid border-track-line rounded-[10px] px-5 py-4 flex flex-col gap-3',
						highlight?.truckId === truck.id && 'order-tracking-highlight',
					)}
				>
					<div className="flex items-center gap-3">
						<div className="w-9 h-9 rounded-[32%] bg-track-indigo-soft text-track-indigo flex items-center justify-center font-bold text-sm flex-none">
							#{truck.number}
						</div>
						<div className="min-w-0">
							<div className="text-sm font-semibold text-track-ink">
								<T
									keyName="orderTracking.truckNumber"
									params={{ number: truck.number }}
								>
									{'Truck #{number}'}
								</T>
							</div>
							{truckIdentity(truck) ? (
								<div className="text-[12.5px] text-track-muted truncate">
									{truckIdentity(truck)}
								</div>
							) : null}
						</div>
					</div>

					{TRACKING_DOCUMENT_GROUPS.map((group) => {
						const docs = truckDocuments(truck, group.steps);

						return (
							<div
								key={group.key}
								className="border-0 border-t border-solid border-track-surface pt-3"
							>
								<div className="text-[12.5px] font-semibold text-track-slate mb-1">
									{t(group.labelKey, group.defaultLabel)}
								</div>
								{docs.length ? (
									docs.map((doc) => (
										<DocumentRow key={`${doc.name}-${doc.url}`} doc={doc} />
									))
								) : (
									<div className="text-[13px] text-track-muted py-1">
										<T keyName="orderTracking.noDocuments">
											No documents uploaded yet
										</T>
									</div>
								)}
							</div>
						);
					})}
				</div>
			))}
		</section>
	);
};

export default TrackingDocuments;
